import ApiClient from "@/api/ApiClient";
import Response from "@/api/models/Response";
import cookiesHandler from "@/composables/cookiesHandler";
import Cookies from "js-cookie";
import { useAuthStore } from "@/stores/auth";
import { useContextStore } from "@/stores/context";
import { translate } from "@/composables/translate";
import { useSessionMessageStore } from "@/stores/sessionMessage";

class AuthUserService {
  #prefixUrl = 'api/auth/';

  constructor() {
    this.api = new ApiClient(this.#prefixUrl);
  }

  getAuthStore() {
    return useAuthStore();
  }

  getContextStore() {
    return useContextStore();
  }

  getSessionMessageStore() {
    return useSessionMessageStore();
  }

  /**
   * Tries to login
   * @param {Object{email:string, password:string}} form 
   * @returns 
   */
  async login(form) {
    const response = await this.api.executeRequest("login", form, this.api.methods.POST);

    if(response.success()){
      const token = response.getHeader('authorization');
      cookiesHandler.save('TM_TOKEN', token);
      
      this.getAuthStore().login(response.getContent().content);
      this.getAuthStore().saveToken(token);

      if(form.remember != undefined && form.remember == true){
        this.rememberCredentials(form.email, form.password);
      }

      await this.createContext();
    }

    return response;
  }

  /**
   * Logouts user
   * @returns 
   */
  async logout() {
    Cookies.remove('TM_TOKEN');
    Cookies.remove('TK_DATE');
    Cookies.remove('aut_data');
    this.getAuthStore().logout();

    return true;
  }

  /**
   * Gathers user profile data
   * @returns 
   */
  async profile(token = '', redirect = true) {
    const response = await this.api.executeRequest("profile", {
      Authorization: token
    }, this.api.methods.POST);
    
    if(response.success()){
      this.getAuthStore().login(response.getContent().content);
      await this.createContext();

    } else {
      const result = await this.attemptLoginByCookies();

      if(!result.success()){
        this.getSessionMessageStore().errorMessage(translate('not authenticated', 'ucfirst'));
      } 
    }

    if (
      redirect && 
      !response.success()
    ) {
      this.api.redirect('/login');
    }
    
    return response;
  }

  /**
   * Attempts to login using cookie token or remembered account credentials on cookies
   * @returns {Object}
   */
  async attemptLogin(){
    if (this.getAuthStore().isAuthenticated || this.getAuthStore().isPerformingLogin)
      return;

    this.getAuthStore().performingLogin(true);

    const token = cookiesHandler.get('TM_TOKEN');

    if(token) {
      return await this.profile(token);

    }

    return await this.attemptLoginByCookies();
  }

  /**
   * Attempts to login by saved cookie
   */
  async attemptLoginByCookies(){
    const email = cookiesHandler.getEncryptedCookie('TME');
    const password = cookiesHandler.getEncryptedCookie('TMP');

    if(
      [undefined,null,false,''].includes(email) ||
      [undefined,null,false,''].includes(password)
    ) {
      return new Response({status: 401});
    }

    return await this.login({
      email: email,
      password: password
    });
  }

  /**
   * Sets a timer for regenerating the logged user token
   * @returns 
   */
  async refreshToken() {
    const response = await this.api.executeRequest('regenerate', {}, this.api.methods.POST, {
      headers: {
        Authorization: `Bearer ${cookiesHandler.getEncryptedCookie('TM_TOKEN')}`
      }
    });

    if(!response.success()){
      this.denyEntry();
      return false;
    }

    const token = response.getHeader('authorization');
    cookiesHandler.save('TM_TOKEN', token); 
    this.getAuthStore().saveToken(token);
  }

  /**
   * Redirects user to login page
   * @returns 
   */
  async denyEntry(){
    // store.dispatch('saveRegeneratorCalled', false);
    // store.dispatch('saveSessionMessage', {
    //   message: 'session time expired, authenticate', 
    //   messageType: 'error'
    // });
    await this.logout(true);
    this.api.redirect('/login');
    return false;
  }

  /**
   * Sets a new Context object to store
   */
  async createContext() {
    const contextStore = useContextStore();

    if (this.getAuthStore().isAuthenticated) {
      const user = this.getAuthStore().getUser;
      contextStore.setLang(await user.getLangIso());
    }
  }

  /**
   * Saves TME & TMP with credentials for automatic login
   * @param {string} email 
   * @param {string} password 
   * @param {boolean} checkCookieExistance - checks if cookie already exists, if it doesn't, return false 
   * @returns {boolean}
   */
  rememberCredentials(email, password, checkCookieExistance = false) {
    if (checkCookieExistance && !cookiesHandler.getEncryptedCookie('TME')) {
      return false;
    }

    cookiesHandler.setEncryptedCookie('TME', email);
    cookiesHandler.setEncryptedCookie('TMP', password);

    return true;
  }

  forgetRememberCredentials() {
    cookiesHandler.delete('TME');
    cookiesHandler.delete('TMP');
  }
}

export default new AuthUserService();
